/**
 * Helpers for filtering and formatting status-log entries in the Status Logs sidebar.
 * @module statusLogHelpers
 */

import moment from "moment";

import { STATUS_LOG_CONFIG } from "../../../../constants/statusLogConfig";
import { StatusLogDataWithId } from "../../../../data-model/statusLogModel";

/** Follow-up type id used when a user saves a free-text "Added notes" entry. */
export const ADDED_NOTES_FOLLOW_UP_TYPE_ID =
  STATUS_LOG_CONFIG.others.followUpTypes[0].value;

/** Canonical display label for Added Notes, derived from the same config entry. */
const ADDED_NOTES_FOLLOW_UP_TYPE_LABEL =
  STATUS_LOG_CONFIG.others.followUpTypes[0].label.toLowerCase();

/**
 * Returns whether a status-log entry is an "Added notes" event.
 *
 * @param entry - Status-log row to classify
 * @returns True when the entry was created as an Added Notes event
 */
export const isAddedNotesEvent = (entry: StatusLogDataWithId): boolean => {
  const typeId = entry.follow_up_type_id?.toLowerCase() ?? "";
  const typeLabel = entry.follow_up_type_label?.toLowerCase() ?? "";
  return (
    typeId === ADDED_NOTES_FOLLOW_UP_TYPE_ID ||
    typeLabel === ADDED_NOTES_FOLLOW_UP_TYPE_LABEL ||
    typeLabel === "added note"
  );
};

/**
 * Splits status-log entries into Added Notes vs all other event types.
 *
 * @param entries - Sorted or unsorted status-log rows
 * @returns Notes entries and non-notes (history) entries
 */
export const splitStatusLogEntries = (
  entries: StatusLogDataWithId[],
): {
  notesEntries: StatusLogDataWithId[];
  historyEntries: StatusLogDataWithId[];
} => {
  const notesEntries: StatusLogDataWithId[] = [];
  const historyEntries: StatusLogDataWithId[] = [];

  entries.forEach((entry) => {
    if (isAddedNotesEvent(entry)) {
      notesEntries.push(entry);
    } else {
      historyEntries.push(entry);
    }
  });

  return { notesEntries, historyEntries };
};

/**
 * Formats a created-at timestamp as relative copy for the Notes list
 * (e.g. "2 hours ago", "Yesterday", "4 months ago").
 *
 * @param createdAt - ISO-8601 timestamp string
 * @returns Human-readable relative time label
 */
export const formatRelativeNoteTime = (createdAt: string): string => {
  const created = moment(createdAt);
  if (!created.isValid()) {
    return "";
  }

  if (created.isSame(moment().subtract(1, "day"), "day")) {
    return "Yesterday";
  }

  return created.fromNow();
};
